// PDM-2748: the microbiology box of the hospitalization list holds up to three condition rows, each a pathogen
// multiselect + a resistance multiselect. Codes within one multiselect are ORed; rows are ANDed by the backend,
// which tests a row's pathogen and resistances on one isolate but lets different rows be satisfied by different
// isolates. Either list of a row may be empty - a pathogen-only row and a resistance-only row are both valid.
//
// Own module for the same reason as the biochemistry helpers: the list layout and the xlsx export hook both need
// these and importing one from the other would close an import cycle.
function microbiologyRow<N extends 1 | 2 | 3>(n: N) {
  return {
    pathogens: `pathogenCodes${n}` as const,
    resistances: `resistanceAtbCodes${n}` as const,
    pathogensLabel: `filter.patient.pathogen${n}` as const,
    resistancesLabel: `filter.patient.resistance${n}` as const,
  };
}
export const MICROBIOLOGY_ROWS = [microbiologyRow(1), microbiologyRow(2), microbiologyRow(3)] as const;
export type MicrobiologyRow = typeof MICROBIOLOGY_ROWS[number];
export type MicrobiologyPart = 'pathogens' | 'resistances';
type MicrobiologyPartName = MicrobiologyRow['pathogens'] | MicrobiologyRow['resistances'];
/** The slice of the hospitalization list filters these helpers read. Structural, so both callers just pass theirs. */
export type MicrobiologyFilterValues = Partial<Record<MicrobiologyPartName, string[]>>;

function isFilled(value: string[] | undefined): value is string[] {
  return value !== undefined && value.length > 0;
}

/** A row with something picked in either multiselect. Unlike biochemistry there is no "complete": one list is enough. */
export function isMicrobiologyRowStarted(patients: MicrobiologyFilterValues, row: MicrobiologyRow): boolean {
  return isFilled(patients[row.pathogens]) || isFilled(patients[row.resistances]);
}

/** Number of conditions set; "on the same day" needs at least two rows to compare. */
export function countMicrobiologyConditions(patients: MicrobiologyFilterValues): number {
  return MICROBIOLOGY_ROWS.filter((row) => isMicrobiologyRowStarted(patients, row)).length;
}

/**
 * Rows leave no gaps: everything still filled in slides up to the first free row, a row moving as one unit so its
 * pathogens and resistances stay together. Applied after every edit of the box, like the other two boxes.
 */
export function packMicrobiologyRows(patients: MicrobiologyFilterValues): MicrobiologyFilterValues {
  const kept = MICROBIOLOGY_ROWS.filter((row) => isMicrobiologyRowStarted(patients, row)).map((row) => ({
    pathogens: isFilled(patients[row.pathogens]) ? patients[row.pathogens] : undefined,
    resistances: isFilled(patients[row.resistances]) ? patients[row.resistances] : undefined,
  }));
  const packed: MicrobiologyFilterValues = {};
  MICROBIOLOGY_ROWS.forEach((row, index) => {
    packed[row.pathogens] = kept[index]?.pathogens;
    packed[row.resistances] = kept[index]?.resistances;
  });
  return packed;
}

/** The microbiology part of the filters after one multiselect of one row was edited, with the rows packed up. */
export function applyMicrobiologyRowChange(
  patients: MicrobiologyFilterValues,
  row: MicrobiologyRow,
  part: MicrobiologyPart,
  newValue: string[] | null
): MicrobiologyFilterValues {
  return packMicrobiologyRows({...patients, [row[part]]: newValue ?? undefined});
}

/** The microbiology part of the filters after a whole row was deleted by its remove button; the rows below move up. */
export function removeMicrobiologyRow(patients: MicrobiologyFilterValues, row: MicrobiologyRow): MicrobiologyFilterValues {
  return packMicrobiologyRows({...patients, [row.pathogens]: undefined, [row.resistances]: undefined});
}

/**
 * Which rows the form shows: the first one always, the next one once the previous row has something in it
 * (analyst decision 2026-09-11 - a resistance-only row opens the next row too), and any row that already holds
 * something, so that a gap arriving from a bookmarked URL or a saved filter never hides a condition.
 */
export function showMicrobiologyRows(patients: MicrobiologyFilterValues): boolean[] {
  return MICROBIOLOGY_ROWS.map(
    (row, index) =>
      index === 0 ||
      isMicrobiologyRowStarted(patients, MICROBIOLOGY_ROWS[index - 1]) ||
      MICROBIOLOGY_ROWS.slice(index).some((later) => isMicrobiologyRowStarted(patients, later))
  );
}

/**
 * Request parameters of the box, one comma separated list per filled multiselect. The list query and the xlsx
 * export both go through here so the two can never disagree about what is being filtered.
 */
export function microbiologyParams(patients: MicrobiologyFilterValues): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};
  MICROBIOLOGY_ROWS.forEach((row) => {
    if (isFilled(patients[row.pathogens])) params[row.pathogens] = patients[row.pathogens]?.join(',');
    if (isFilled(patients[row.resistances])) params[row.resistances] = patients[row.resistances]?.join(',');
  });
  return params;
}
